import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Common/Modal';
import Toast from '../components/Common/Toast';
import ChangePasswordModal from './ChangePasswordModal';
import Icon from './Icon';
import { Button } from './ui';
import './dashboard.css';

const DashContext = createContext(null);
export const useDash = () => useContext(DashContext);

/**
 * Sidebar layout shared by the admin and partner dashboards. The nav decides what each role can
 * reach; the API enforces it (admin routes need an admin token, /api/me is scoped to the token).
 *
 * props.nav = [{ group?, to, label, icon, end?, badge? }]
 */
const DashboardShell = ({ nav, roleLabel, roleNote, orgName, userName }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '' });
  const [confirmState, setConfirmState] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => setOpen(false), [location.pathname]);

  const notify = useCallback((message, type = 'success') => setToast({ message, type }), []);
  const confirm = useCallback(
    (opts) => new Promise((resolve) => setConfirmState({ ...opts, resolve })),
    [],
  );
  const settle = (answer) => {
    confirmState?.resolve(answer);
    setConfirmState(null);
  };
  const ctx = useMemo(() => ({ notify, confirm }), [notify, confirm]);

  const groups = [];
  nav.forEach((item) => {
    const g = item.group || '';
    const last = groups[groups.length - 1];
    if (last && last.name === g) last.items.push(item);
    else groups.push({ name: g, items: [item] });
  });

  const initials = (userName || '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <DashContext.Provider value={ctx}>
      <div className={`ds-app ${open ? 'is-open' : ''}`}>
        <header className="ds-mobilebar">
          <button type="button" className="ds-iconbtn" aria-label="Open menu" aria-expanded={open} onClick={() => setOpen(true)}>
            <Icon name="menu" />
          </button>
          <strong>The Comparison <span>Engine</span></strong>
        </header>
        {open && <button type="button" className="ds-scrim" aria-label="Close menu" onClick={() => setOpen(false)} />}

        <aside className="ds-side" aria-label="Dashboard menu">
          <div className="ds-brand">
            <strong>The Comparison <span>Engine</span></strong>
            <em>PK</em>
            <button type="button" className="ds-iconbtn ds-close" aria-label="Close menu" onClick={() => setOpen(false)}>
              <Icon name="close" />
            </button>
          </div>
          <p className="ds-brand-sub">Partner portal</p>

          <div className="ds-role">
            <span>{roleLabel}</span>
            {orgName && <b>{orgName}</b>}
            <small>{roleNote}</small>
          </div>

          <nav>
            {groups.map((g) => (
              <div key={g.name || 'main'} className="ds-navgroup">
                {g.name && <p>{g.name}</p>}
                {g.items.map((item) => (
                  <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => `ds-navlink ${isActive ? 'is-active' : ''}`}>
                    <Icon name={item.icon} />
                    <span>{item.label}</span>
                    {item.badge ? <em>{item.badge}</em> : null}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          <div className="ds-navgroup ds-bottom">
            <p>Account</p>
            <Link to="/" className="ds-navlink" title="Public site" aria-label="Public site">
              <Icon name="globe" />
              <span>Public site</span>
            </Link>
            <button type="button" className="ds-navlink" title="Change password" aria-label="Change password" onClick={() => setPwOpen(true)}>
              <Icon name="lock" />
              <span>Change password</span>
            </button>
            <button type="button" className="ds-navlink" title="Log out" aria-label="Log out" onClick={handleLogout}>
              <Icon name="logout" />
              <span>Log out</span>
            </button>
          </div>

          <div className="ds-user">
            <span className="ds-avatar" aria-hidden="true">{initials}</span>
            <div>
              <strong>{userName}</strong>
            </div>
          </div>
        </aside>

        <main className="ds-main">
          <Outlet />
        </main>
      </div>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} onDone={(m) => notify(m)} />
      <Modal isOpen={!!confirmState} title={confirmState?.title || 'Are you sure?'} onClose={() => settle(false)}>
        <p className="ds-confirm-text">{confirmState?.message}</p>
        <div className="ds-modal-actions">
          <Button onClick={() => settle(false)}>Cancel</Button>
          <Button variant={confirmState?.danger ? 'danger' : 'primary'} onClick={() => settle(true)}>
            {confirmState?.confirmLabel || 'Confirm'}
          </Button>
        </div>
      </Modal>
      {toast.message && <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '' })} />}
    </DashContext.Provider>
  );
};

export default DashboardShell;
